import React, { useEffect, useState, useMemo } from "react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Eye, RefreshCw, Download, FileSpreadsheet, ShieldCheck } from "lucide-react";

const STATUSES = ["yeni", "iletisimde", "tamamlandi", "iptal"];
const STATUS_LABEL = { yeni: "Yeni", iletisimde: "İletişimde", tamamlandi: "Tamamlandı", iptal: "İptal" };
const STATUS_COLOR = {
  yeni: "bg-red-500/20 text-red-300 border-red-500/30",
  iletisimde: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  tamamlandi: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  iptal: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30",
};

function todayISO(offsetDays = 0) {
  const d = new Date(); d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

const emptyPolicy = {
  customer_name: "", customer_phone: "", customer_tc: "",
  product_key: "", policy_number: "", start_date: "",
  end_date: "", premium: 0, notes: "",
};

export default function RequestsPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [policyReq, setPolicyReq] = useState(null); // request being converted
  const [pf, setPf] = useState(emptyPolicy);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const openPolicyFor = (req) => {
    const d = req.data || {};
    setPf({
      customer_name: d.ad_soyad || "",
      customer_phone: d.telefon || "",
      customer_tc: d.tc_kimlik || "",
      product_key: req.product_key || "",
      policy_number: "",
      start_date: todayISO(0),
      end_date: todayISO(365),
      premium: 0,
      notes: d.notlar || "",
    });
    setPolicyReq(req);
  };

  const closePolicyDialog = () => {
    setPolicyReq(null);
    setPf(emptyPolicy);
    load();
  };

  const savePolicy = async () => {
    if (!pf.customer_name || !pf.product_key || !pf.policy_number || !pf.start_date || !pf.end_date) {
      toast.error("Zorunlu alanları doldurun (Müşteri, Ürün, Poliçe No, Başlangıç, Bitiş)"); return;
    }
    setSaving(true);
    try {
      await api.post("/policies", { ...pf, source_request_id: policyReq.id });
      toast.success("Poliçe kaydedildi ve talep tamamlandı olarak işaretlendi");
      closePolicyDialog();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (row, status) => {
    if (status === "tamamlandi") {
      if (row.policy_id) {
        // Already has a policy — just confirm status
        try { await api.put(`/requests/${row.id}`, { status }); toast.success("Durum güncellendi"); load(); }
        catch (e) { toast.error(apiError(e)); }
        return;
      }
      openPolicyFor(row);
      return;
    }
    try {
      await api.put(`/requests/${row.id}`, { status });
      toast.success("Durum güncellendi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Bu talebi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/requests/${id}`);
      toast.success("Talep silindi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportFile = async (format) => {
    try {
      const token = localStorage.getItem("asg_token");
      const res = await fetch(`${API}/exports/requests?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Dışa aktarma başarısız");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `talepler-${new Date().toISOString().slice(0,10)}.${format === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("İndirildi");
    } catch (e) { toast.error(e.message); }
  };

  const setPfField = (k, v) => setPf((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">Talepler</h1>
          <p className="text-zinc-400 text-sm mt-1">Site üzerinden gelen sigorta talepleri. "Tamamlandı" seçildiğinde poliçe bilgileri istenir.</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
        <Button variant="outline" onClick={load} data-testid="requests-refresh" className="border-white/10 text-white bg-transparent hover:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-2" /> Yenile
        </Button>
        <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="requests-export-xlsx" className="border-white/10 text-white bg-transparent hover:bg-white/5 ml-2">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
        </Button>
        <Button variant="outline" onClick={() => exportFile("csv")} data-testid="requests-export-csv" className="border-white/10 text-white bg-transparent hover:bg-white/5 ml-2">
          <Download className="w-4 h-4 mr-2" /> CSV
        </Button>
        </div>
      </div>

      <div className="asg-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-zinc-400 uppercase tracking-widest text-xs">
              <tr>
                <th className="text-left p-4">Tarih</th>
                <th className="text-left p-4">Müşteri</th>
                <th className="text-left p-4">Ürün</th>
                <th className="text-left p-4">Personel</th>
                <th className="text-left p-4">Durum</th>
                <th className="text-right p-4">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-zinc-500">Henüz talep yok</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`req-row-${r.id}`}>
                  <td className="p-4 text-zinc-400 whitespace-nowrap">{new Date(r.created_at).toLocaleString("tr-TR")}</td>
                  <td className="p-4 text-white">
                    {r.data?.ad_soyad || r.data?.telefon || "—"}
                    {r.policy_id && <div className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5 ml-2"><ShieldCheck className="w-3 h-3" /> Poliçe</div>}
                  </td>
                  <td className="p-4 text-zinc-300">{r.product_title}</td>
                  <td className="p-4 text-zinc-300">{r.staff_name || "—"}</td>
                  <td className="p-4">
                    <Select value={r.status} onValueChange={(v) => updateStatus(r, v)}>
                      <SelectTrigger className={`h-8 w-36 border ${STATUS_COLOR[r.status] || "border-white/10"}`} data-testid={`req-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[color:var(--asg-surface)] text-white border-white/10">
                        {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`req-status-opt-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => setDetail(r)} className="text-zinc-400 hover:text-white mr-2" data-testid={`req-view-${r.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(r.id)} className="text-zinc-400 hover:text-red-400" data-testid={`req-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[color:var(--asg-surface)] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Talep Detayı</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-3 p-4 rounded-lg bg-white/5">
                <div><div className="text-zinc-500 text-xs">Ürün</div><div>{detail.product_title}</div></div>
                <div><div className="text-zinc-500 text-xs">Personel</div><div>{detail.staff_name || "—"}</div></div>
                <div><div className="text-zinc-500 text-xs">Tarih</div><div>{new Date(detail.created_at).toLocaleString("tr-TR")}</div></div>
              </div>
              <div className="grid gap-2">
                {Object.entries(detail.data || {}).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-3 p-2 border-b border-white/5">
                    <div className="text-zinc-500 capitalize">{k.replace(/_/g, " ")}</div>
                    <div className="col-span-2 text-white">{String(v || "—")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!policyReq} onOpenChange={(o) => { if (!o) closePolicyDialog(); }}>
        <DialogContent className="bg-[color:var(--asg-surface)] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-from-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 asg-primary" /> Poliçe Bilgilerini Gir
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Talep "Tamamlandı" olarak işaretlenmek için poliçe bilgilerinin girilmesi gerekir. Müşteri bilgileri talepten otomatik dolduruldu.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2"><Label>Müşteri Ad Soyad <span className="asg-primary">*</span></Label>
              <Input value={pf.customer_name} onChange={(e) => setPfField("customer_name", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-name" />
            </div>
            <div className="grid gap-2"><Label>Telefon</Label>
              <Input value={pf.customer_phone} onChange={(e) => setPfField("customer_phone", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-phone" />
            </div>
            <div className="grid gap-2"><Label>T.C. Kimlik</Label>
              <Input value={pf.customer_tc} onChange={(e) => setPfField("customer_tc", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
            </div>
            <div className="grid gap-2"><Label>Poliçe No <span className="asg-primary">*</span></Label>
              <Input value={pf.policy_number} onChange={(e) => setPfField("policy_number", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" placeholder="Sigorta şirketinden aldığınız numara" data-testid="pf-policy-number" />
            </div>
            <div className="grid gap-2"><Label>Ürün <span className="asg-primary">*</span></Label>
              <Select value={pf.product_key} onValueChange={(v) => setPfField("product_key", v)}>
                <SelectTrigger className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-product"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-[color:var(--asg-surface)] border-white/10 text-white">
                  {products.map((p) => <SelectItem key={p.key} value={p.key}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2"><Label>Prim (₺)</Label>
              <Input type="number" value={pf.premium} onChange={(e) => setPfField("premium", parseFloat(e.target.value) || 0)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-premium" />
            </div>
            <div className="grid gap-2"><Label>Başlangıç Tarihi <span className="asg-primary">*</span></Label>
              <Input type="date" value={pf.start_date} onChange={(e) => setPfField("start_date", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-start" />
            </div>
            <div className="grid gap-2"><Label>Bitiş Tarihi <span className="asg-primary">*</span></Label>
              <Input type="date" value={pf.end_date} onChange={(e) => setPfField("end_date", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="pf-end" />
            </div>
            <div className="sm:col-span-2 grid gap-2"><Label>Notlar</Label>
              <Textarea value={pf.notes} onChange={(e) => setPfField("notes", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closePolicyDialog} className="text-zinc-300 hover:text-white">Vazgeç</Button>
            <Button onClick={savePolicy} disabled={saving} className="asg-btn-primary rounded-full border-0" data-testid="pf-save">
              {saving ? "Kaydediliyor..." : "Poliçeyi Kaydet & Talebi Tamamla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
